import { useI18n } from "@/lib/i18n/i18n_provider";
import { CheckoutStripeInput } from "@/offering/template_offering_checkout_stripe_base";
import { TextFormField } from "./text_form_field";
import { FormikErrors, FormikTouched } from "formik";
import { ForwardedRef, SyntheticEvent, useCallback, useState } from "react";
import { forwardRef } from "react";
import React from "react";
import { isTenDigits } from "@/lib/validation/string";
import { APIError } from "@/data/api";

interface MembershipNumberInput {
  membershipNumber?: string;
}

const useValidateMembershipNumber = () => {
  return useCallback(async (data: string) => {
    const response = await fetch(`/api/v3/malin_equinox_members/validate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ membership_number: data }),
    });

    const body = await response.json();

    if (response.status >= 200 && response.status < 300) {
      return body;
    }

    throw new APIError(body.message || body.errors?.[0], body.code);
  }, []);
};

interface EquinoxMembershipNumber {
  values: MembershipNumberInput;
  errors: FormikErrors<MembershipNumberInput>;
  touched: FormikTouched<MembershipNumberInput>;
  setFieldValue: (field: string, value: any) => void;
  onChangeMembershipNumber: (value?: string) => void;
  setFieldTouched?: (
    field: string
  ) => Promise<void> | Promise<FormikErrors<CheckoutStripeInput>>;
  errorFieldId?: keyof CheckoutStripeInput;
  onErrorTextRef?: (
    ref: HTMLElement | null,
    errorId: keyof MembershipNumberInput
  ) => void;
  onInputBorderRef?: (
    ref: HTMLElement | null,
    errorId: keyof MembershipNumberInput
  ) => void;
  setFieldError: (field: string, value: string | undefined) => void;
}

export const EquinoxMembershipNumber = forwardRef(
  (props: EquinoxMembershipNumber, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      values,
      errors,
      touched,
      onChangeMembershipNumber,
      setFieldTouched,
      errorFieldId,
      onErrorTextRef,
      onInputBorderRef,
      setFieldError,
    } = props;

    const [membershipNumber, setMembershipNumber] = useState<string>();
    const validateMembershipNumber = useValidateMembershipNumber();

    const onMembershipNumberChange = useCallback(
      (membershipNumber: string) => {
        const numbersOnly = membershipNumber.replace(/[^0-9]/g, "");
        onChangeMembershipNumber(numbersOnly);
      },
      [onChangeMembershipNumber]
    );

    const onMembershipNumberFocusOut = useCallback(
      async (event: SyntheticEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        setFieldTouched?.("membershipNumber");
        if (isTenDigits(value) && value !== membershipNumber) {
          setMembershipNumber(value);
          onMembershipNumberChange(value);
          const result = await validateMembershipNumber(value);
          if (!result.success && result.message) {
            setFieldError("membershipNumber", result.message);
          }
        }
      },
      [
        membershipNumber,
        onMembershipNumberChange,
        setFieldError,
        setFieldTouched,
        validateMembershipNumber,
      ]
    );

    const { fbt } = useI18n();

    return (
      <div
        className="col-12 col-lg-6 mr-lg-2 pe-lg-3"
        ref={errorFieldId === "membershipNumber" ? ref : undefined}
      >
        <TextFormField
          name="membershipNumber"
          label={fbt("Member ID Barcode")}
          value={values.membershipNumber || ""}
          onChange={onMembershipNumberChange}
          error={touched.membershipNumber && errors.membershipNumber}
          onBlur={onMembershipNumberFocusOut}
          errorRef={(ref) => onErrorTextRef?.(ref, "membershipNumber")}
          inputRef={(ref) => onInputBorderRef?.(ref, "membershipNumber")}
        />
      </div>
    );
  }
);
